import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Select, Textarea } from "@/components/ui/input";
import { type OwnerRef, api, q } from "@/lib/api";
import type { AgentDTO } from "@mm-sales/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

/** "Tim Hills · MYMANAGER D — Downtown (Flagship)": who the account is, in MyManager terms. */
export const agentLabel = (a: AgentDTO) => {
  if (a.kind === "partner") return `${a.name} (partner)`;
  const where = [a.mmOrgName, a.mmLocationName].filter(Boolean).join(" — ");
  if (where) return `${a.name} · ${where}`;
  return a.role === "superadmin" ? `${a.name} · super-admin` : a.name;
};

/** The line under an owner's name: "MYMANAGER D — Downtown", "Partner" or "Super-admin". */
export const ownerWhere = (o: OwnerRef) => {
  if (o.kind === "partner") return "Partner";
  const where = [o.orgName, o.locationName].filter(Boolean).join(" — ");
  if (where) return where;
  return o.role === "superadmin" ? "Super-admin" : "";
};

/** Move a prospect to another account (managers and superadmins). */
export function AssignButton({
  prospectId,
  currentOwner,
  onDone,
  size,
  variant = "outline",
}: {
  prospectId: string;
  currentOwner: string;
  onDone: () => void;
  size?: "sm";
  variant?: "outline" | "ghost";
}) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const agents = useQuery({
    queryKey: ["agents"],
    queryFn: () => q.agents(),
    enabled: open,
  });
  const m = useMutation({
    mutationFn: () =>
      api.post(`/v1/prospects/${prospectId}/assign`, {
        toAgentId: to,
        reason: "manual",
        note: note || undefined,
      }),
    onSuccess: () => {
      setOpen(false);
      setTo("");
      setNote("");
      onDone();
    },
  });
  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)}>
        Reassign
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Reassign prospect">
        <div className="space-y-4">
          <Field label="Assign to">
            <Select value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Choose an account…</option>
              {(agents.data?.items ?? [])
                .filter((a: AgentDTO) => a.id !== currentOwner && a.status === "active")
                .map((a: AgentDTO) => (
                  <option key={a.id} value={a.id}>
                    {agentLabel(a)}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Note (optional)">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!to} loading={m.isPending} onClick={() => m.mutate()}>
              Reassign
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
